import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, renderList as _renderList, Fragment as _Fragment, withModifiers as _withModifiers, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { class: "sib" }
const _hoisted_2 = { class: "sib__card" }
const _hoisted_3 = { class: "sib__header" }
const _hoisted_4 = { class: "sib__title" }
const _hoisted_5 = {
  key: 0,
  class: "sib__badge"
}
const _hoisted_6 = ["disabled"]
const _hoisted_7 = {
  key: 0,
  class: "sib__error"
}
const _hoisted_8 = {
  key: 1,
  class: "sib__empty"
}
const _hoisted_9 = {
  key: 2,
  class: "sib__section"
}
const _hoisted_10 = {
  key: 3,
  class: "sib__list"
}
const _hoisted_11 = { class: "sib__main" }
const _hoisted_12 = { class: "sib__name" }
const _hoisted_13 = { class: "sib__meta" }
const _hoisted_14 = { class: "sib__actions" }
const _hoisted_15 = ["disabled", "onClick"]
const _hoisted_16 = ["disabled", "onClick"]
const _hoisted_17 = {
  key: 4,
  class: "sib__empty"
}
const _hoisted_18 = {
  key: 5,
  class: "sib__list"
}
const _hoisted_19 = ["onClick"]
const _hoisted_20 = { class: "sib__main" }
const _hoisted_21 = { class: "sib__name" }
const _hoisted_22 = { class: "sib__meta" }
const _hoisted_23 = ["disabled", "onClick"]
const _hoisted_24 = ["disabled", "onClick"]

import { computed, onBeforeUnmount, onMounted, reactive, ref } from "vue";
import {
  acceptFriendRequest,
  acceptSpeakingInvite,
  declineFriendRequest,
  declineSpeakingInvite,
  getSocialInbox,
  markAllNotificationsRead,
  markNotificationRead,
} from "@/api/social";
import { openSpeakingLobby } from "@/api/speakingRooms";
import { handleError } from "@/services/errorHandler";
import type { PublicUser, SocialInbox, SocialNotification, SpeakingInviteItem } from "@/types";

const POLL_MS = 8000;


export default /*@__PURE__*/_defineComponent({
  __name: 'SocialInbox',
  setup(__props) {

const loading = ref(true);
const busy = ref(false);
const errorMessage = ref<string | null>(null);
const handledRequestIds = ref<number[]>([]);
const inbox = reactive<SocialInbox>({
  unread_count: 0,
  notifications: [],
  speaking_invites: [],
});

let pollId: ReturnType<typeof setInterval> | null = null;

const hasItems = computed(
  () => inbox.notifications.length + inbox.speaking_invites.length > 0
);

function fa(value: number): string {
  return String(value).replace(/\d/g, (d) => "۰۱۲۳۴۵۶۷۸۹"[Number(d)]);
}

function personName(user: PublicUser): string {
  return user.full_name?.trim() || user.username;
}

function payloadId(item: SocialNotification, key: string): number | null {
  const value = item.payload?.[key];
  return typeof value === "number" ? value : null;
}

function isIncomingFriend(item: SocialNotification): boolean {
  const requestId = payloadId(item, "request_id");
  return (
    item.type === "friend_request" &&
    requestId != null &&
    !handledRequestIds.value.includes(requestId)
  );
}

async function load(silent = false) {
  if (!silent) loading.value = true;
  try {
    const data = await getSocialInbox();
    inbox.unread_count = data.unread_count;
    inbox.notifications = data.notifications;
    inbox.speaking_invites = data.speaking_invites;
    errorMessage.value = null;
  } catch (e) {
    if (!silent) errorMessage.value = handleError(e, "SocialInbox.load");
  } finally {
    loading.value = false;
  }
}

async function markAll() {
  busy.value = true;
  try {
    await markAllNotificationsRead();
    await load(true);
  } catch (e) {
    errorMessage.value = handleError(e, "SocialInbox.markAll");
  } finally {
    busy.value = false;
  }
}

async function acceptInvite(invite: SpeakingInviteItem) {
  busy.value = true;
  try {
    const accepted = await acceptSpeakingInvite(invite.id);
    openSpeakingLobby(accepted.room_code, "attendee");
    await load(true);
  } catch (e) {
    errorMessage.value = handleError(e, "SocialInbox.acceptInvite");
  } finally {
    busy.value = false;
  }
}

async function declineInvite(inviteId: number) {
  busy.value = true;
  try {
    await declineSpeakingInvite(inviteId);
    await load(true);
  } catch (e) {
    errorMessage.value = handleError(e, "SocialInbox.declineInvite");
  } finally {
    busy.value = false;
  }
}

async function onNotification(item: SocialNotification) {
  if (isIncomingFriend(item) || item.type === "speaking_invite") return;
  if (!item.read_at) {
    try {
      await markNotificationRead(item.id);
      await load(true);
    } catch {
      /* ignore */
    }
  }
}

async function acceptFromPayload(item: SocialNotification) {
  const requestId = payloadId(item, "request_id");
  if (!requestId) return;
  busy.value = true;
  try {
    await acceptFriendRequest(requestId);
    handledRequestIds.value = [...handledRequestIds.value, requestId];
    await markNotificationRead(item.id).catch(() => undefined);
    await load(true);
  } catch (e) {
    errorMessage.value = handleError(e, "SocialInbox.acceptFriend");
  } finally {
    busy.value = false;
  }
}

async function declineFromPayload(item: SocialNotification) {
  const requestId = payloadId(item, "request_id");
  if (!requestId) return;
  busy.value = true;
  try {
    await declineFriendRequest(requestId);
    handledRequestIds.value = [...handledRequestIds.value, requestId];
    await markNotificationRead(item.id).catch(() => undefined);
    await load(true);
  } catch (e) {
    errorMessage.value = handleError(e, "SocialInbox.declineFriend");
  } finally {
    busy.value = false;
  }
}

onMounted(() => {
  void load();
  pollId = setInterval(() => {
    void load(true);
  }, POLL_MS);
});

onBeforeUnmount(() => {
  if (pollId) clearInterval(pollId);
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("header", _hoisted_3, [
        _cache[3] || (_cache[3] = _createElementVNode("i", {
          class: "bi bi-inbox-fill sib__header-icon",
          "aria-hidden": "true"
        }, null, -1)),
        _createElementVNode("div", null, [
          _createElementVNode("h2", _hoisted_4, [
            _cache[1] || (_cache[1] = _createTextVNode(" پیام‌ها ", -1)),
            (inbox.unread_count)
              ? (_openBlock(), _createElementBlock("span", _hoisted_5, _toDisplayString(fa(inbox.unread_count)), 1))
              : _createCommentVNode("", true)
          ]),
          _cache[2] || (_cache[2] = _createElementVNode("p", { class: "sib__subtitle" }, "دعوت اسپیکینگ و رویدادهای دوستی", -1))
        ]),
        (inbox.unread_count)
          ? (_openBlock(), _createElementBlock("button", {
              key: 0,
              type: "button",
              class: "sib__readall",
              disabled: busy.value,
              onClick: markAll
            }, " خوانده شد ", 8, _hoisted_6))
          : _createCommentVNode("", true)
      ]),
      (errorMessage.value)
        ? (_openBlock(), _createElementBlock("p", _hoisted_7, [
            _createTextVNode(_toDisplayString(errorMessage.value) + " ", 1),
            _createElementVNode("button", {
              type: "button",
              class: "sib__retry",
              onClick: load
            }, "تلاش مجدد")
          ]))
        : (loading.value && !hasItems.value)
          ? (_openBlock(), _createElementBlock("p", _hoisted_8, "در حال بارگذاری…"))
          : _createCommentVNode("", true),
      (inbox.speaking_invites.length)
        ? (_openBlock(), _createElementBlock("h3", _hoisted_9, "دعوت اسپیکینگ"))
        : _createCommentVNode("", true),
      (inbox.speaking_invites.length)
        ? (_openBlock(), _createElementBlock("ul", _hoisted_10, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(inbox.speaking_invites, (invite) => {
              return (_openBlock(), _createElementBlock("li", {
                key: 'inv-' + invite.id,
                class: "sib__row"
              }, [
                _createElementVNode("div", _hoisted_11, [
                  _createElementVNode("span", _hoisted_12, _toDisplayString(personName(invite.from_user)), 1),
                  _createElementVNode("span", _hoisted_13, "اتاق " + _toDisplayString(invite.room_code), 1)
                ]),
                _createElementVNode("div", _hoisted_14, [
                  _createElementVNode("button", {
                    type: "button",
                    class: "sib__join",
                    disabled: busy.value,
                    onClick: ($event: any) => (acceptInvite(invite))
                  }, " ورود ", 8, _hoisted_15),
                  _createElementVNode("button", {
                    type: "button",
                    class: "sib__remove",
                    disabled: busy.value,
                    onClick: ($event: any) => (declineInvite(invite.id))
                  }, " رد ", 8, _hoisted_16)
                ])
              ]))
            }), 128))
          ]))
        : _createCommentVNode("", true),
      _cache[4] || (_cache[4] = _createElementVNode("h3", { class: "sib__section" }, "رویدادها", -1)),
      (!inbox.notifications.length)
        ? (_openBlock(), _createElementBlock("p", _hoisted_17, "پیامی نیست."))
        : (_openBlock(), _createElementBlock("ul", _hoisted_18, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(inbox.notifications, (item) => {
              return (_openBlock(), _createElementBlock("li", {
                key: item.id,
                class: _normalizeClass(["sib__row", { 'sib__row--unread': !item.read_at }]),
                onClick: ($event: any) => (onNotification(item))
              }, [
                _createElementVNode("div", _hoisted_20, [
                  _createElementVNode("span", _hoisted_21, _toDisplayString(item.title), 1),
                  _createElementVNode("span", _hoisted_22, _toDisplayString(item.body), 1)
                ]),
                (isIncomingFriend(item))
                  ? (_openBlock(), _createElementBlock("div", {
                      key: 0,
                      class: "sib__actions",
                      onClick: _cache[0] || (_cache[0] = _withModifiers(() => {}, ["stop"]))
                    }, [
                      _createElementVNode("button", {
                        type: "button",
                        class: "sib__join",
                        disabled: busy.value,
                        onClick: ($event: any) => (acceptFromPayload(item))
                      }, " پذیرش ", 8, _hoisted_23),
                      _createElementVNode("button", {
                        type: "button",
                        class: "sib__remove",
                        disabled: busy.value,
                        onClick: ($event: any) => (declineFromPayload(item))
                      }, " رد ", 8, _hoisted_24)
                    ]))
                  : _createCommentVNode("", true)
              ], 10, _hoisted_19))
            }), 128))
          ]))
    ])
  ]))
}
}

})